<template>
    <form @submit.prevent="submit">
        <div v-if="!isCustomer && replyAsEnabled" class="mt-2 mb-2">
            <quotes-contact-input
                :key="replyAsPickerKey"
                :options="replyAsOptions"
                :salesperson-id="replyAsDefaultUserId"
                name="reply_as_user_id"
                @update:modelValue="replyAsUserId = $event"
            ></quotes-contact-input>
        </div>
        <div class="quote-chat-composer">
            <div v-if="attachment" class="quote-chat-composer-chip">
                <img
                    v-if="attachment.previewUrl"
                    class="quote-chat-composer-chip-thumbnail"
                    :src="attachment.previewUrl"
                    alt=""
                />
                <div v-else class="quote-chat-composer-chip-icon">
                    <i class="fa-regular fa-file-pdf" aria-hidden="true"></i>
                </div>
                <div class="quote-chat-composer-chip-details">
                    <span class="quote-chat-composer-chip-name">{{ attachment.name }}</span>
                    <small class="text-muted">{{ formatFileSize(attachment.size) }}</small>
                    <div
                        v-if="attachment.uploading"
                        class="quote-chat-composer-chip-progress"
                        role="progressbar"
                        :aria-valuenow="Math.round(attachment.progress * 100)"
                        aria-valuemin="0"
                        aria-valuemax="100"
                    >
                        <div :style="{ width: `${attachment.progress * 100}%` }"></div>
                    </div>
                </div>
                <button
                    type="button"
                    class="quote-chat-composer-chip-remove"
                    aria-label="Ta bort bilagan"
                    :disabled="submitting"
                    @click="removeAttachment"
                >
                    <i class="fa-regular fa-xmark" aria-hidden="true"></i>
                </button>
            </div>
            <textarea
                ref="textareaRef"
                class="quote-chat-composer-input"
                rows="1"
                placeholder="Skriv ett meddelande…"
                v-model="message"
                @input="growTextarea"
            ></textarea>
            <div class="quote-chat-composer-toolbar">
                <div class="quote-chat-composer-attachment-control">
                    <template v-if="canAttach">
                        <input
                            ref="fileInputRef"
                            type="file"
                            class="d-none"
                            :accept="attachmentAccept"
                            @change="pickAttachment"
                        />
                        <button
                            type="button"
                            class="quote-chat-composer-attach"
                            aria-label="Bifoga fil"
                            :disabled="submitting"
                            @click="fileInputRef.click()"
                        >
                            <i class="fa-regular fa-paperclip" aria-hidden="true"></i>
                        </button>
                    </template>
                </div>
                <button
                    type="submit"
                    class="btn signbuttoninline quote-chat-composer-send"
                    :aria-label="chatAvailableForCustomer ? `Skicka till ${seller}` : 'Chatten är stängd'"
                    :disabled="!canSend"
                >
                    <div
                        v-if="submitting"
                        class="spinner-border spinner-border-sm mr-2"
                    ></div>
                    <span v-if="chatAvailableForCustomer">Skicka</span>
                    <span v-else>Chatten är stängd</span>
                    <i
                        v-if="chatAvailableForCustomer"
                        class="fa-regular fa-paper-plane"
                        aria-hidden="true"
                    ></i>
                </button>
            </div>
        </div>
        <div v-if="errorMessage" class="text-danger small mt-2" role="alert">
            {{ errorMessage }}
        </div>
        <div class="quote-chat-composer-response-time">
            <small class="text-muted" v-if="chatAvailableForCustomer">{{
                chatResponseTimeText
            }}</small>
        </div>
    </form>
</template>

<style lang="scss" scoped>
.quote-chat-composer {
    border: 1px solid #dfe3e8;
    border-radius: 12px;
    background: #fff;
    transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;

    &:focus-within {
        border-color: color-mix(in srgb, var(--primary-color, #007bff) 60%, #dfe3e8);
        box-shadow: 0 0 0 1px color-mix(in srgb, var(--primary-color, #007bff) 12%, transparent);
    }
}

.quote-chat-composer-input {
    display: block;
    width: 100%;
    min-height: 44px;
    max-height: 12em;
    padding: 12px 14px 6px;
    border: 0;
    outline: 0;
    resize: none;
    background: transparent;
    font-size: 16px;
    line-height: 24px;

    &::placeholder {
        color: #9299a2;
    }
}

.quote-chat-composer-toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    padding: 2px 8px 8px;
}

.quote-chat-composer-attachment-control {
    display: flex;
    align-items: center;
}

.quote-chat-composer-attach,
.quote-chat-composer-chip-remove {
    border: 0;
    background: transparent;
    color: #6c757d;
    padding: 6px 8px;
    border-radius: 8px;

    &:hover:not(:disabled) {
        background: #f1f3f5;
        color: #343a40;
    }
}

.quote-chat-composer-attach {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 44px;
    height: 40px;
    padding: 0;
    font-size: 18px;
}

.quote-chat-composer-send {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    min-height: 40px;
    margin: 0;
    padding: 8px 14px;
    border: 0;
    border-radius: 8px;
    box-shadow: none;
    font-size: 14px;
    font-weight: 600;
    line-height: 20px;

    &:focus-visible {
        outline: 2px solid var(--primary-color, #007bff);
        outline-offset: 3px;
    }

    &:disabled {
        opacity: 0.5;
    }
}

.quote-chat-composer-response-time {
    margin-top: 8px;
    text-align: center;
    line-height: 1.4;

    small {
        font-size: 12px;
    }
}

.quote-chat-composer-chip {
    display: flex;
    align-items: center;
    gap: 10px;
    margin: 8px 8px 0;
    padding: 8px;
    border-radius: 10px;
    background: #f8f9fa;
}

.quote-chat-composer-chip-thumbnail,
.quote-chat-composer-chip-icon {
    flex: none;
    width: 40px;
    height: 40px;
    border-radius: 6px;
}

.quote-chat-composer-chip-thumbnail {
    object-fit: cover;
}

.quote-chat-composer-chip-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    background: #fff;
    color: #dc3545;
    font-size: 1.2em;
}

.quote-chat-composer-chip-details {
    display: flex;
    flex-direction: column;
    flex: 1;
    min-width: 0;
}

.quote-chat-composer-chip-name {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.quote-chat-composer-chip-progress {
    height: 3px;
    margin-top: 4px;
    border-radius: 2px;
    background: #e9ecef;
    overflow: hidden;

    div {
        height: 100%;
        background: var(--primary-color, #007bff);
        transition: width 0.2s linear;
    }
}
</style>

<script setup>
import { route } from "ziggy";
import { computed, onUnmounted, reactive, ref, watch } from "vue";
import axios from "axios";
import { v4 as uuidv4 } from "uuid";
import { formatFileSize } from "./file-size";
import { uploadToPresignedUrl } from "./presigned-upload";
const emit = defineEmits(["submitted"]);
const props = defineProps({
    quoteKey: String,
    isCustomer: {
        type: Boolean,
        default: false,
    },
    isTemplate: {
        type: Boolean,
        default: false,
    },
    chatAvailableForCustomer: {
        type: Boolean,
        default: true,
    },
    chatAttachmentsAvailable: {
        type: Boolean,
        default: false,
    },
    attachmentAccept: {
        type: String,
        default: "",
    },
    chatResponseTimeText: {
        type: String,
    },
    seller: {
        type: String,
    },
    replyAsEnabled: {
        type: Boolean,
        default: false,
    },
    replyAsOptions: {
        type: Array,
        default: () => [],
    },
    replyAsDefaultUserId: {
        type: Number,
        default: null,
    },
});
const submitting = ref(false);
const message = ref(null);
const attachment = ref(null);
const errorMessage = ref(null);
const textareaRef = ref(null);
const fileInputRef = ref(null);
const replyAsUserId = ref(props.replyAsDefaultUserId);
const replyAsPickerKey = ref(0);
const idempotencyKey = ref(null);
const idempotencyPayload = ref(null);
const attachmentsBecameUnavailable = ref(false);
const canAttach = computed(
    () =>
        props.isCustomer &&
        props.chatAvailableForCustomer &&
        props.chatAttachmentsAvailable &&
        !attachmentsBecameUnavailable.value
);
const failureMessages = {
    rate_limited: "Du har skickat många filer. Försök igen om en stund.",
    file_rejected: "Filen kunde inte skickas. Välj en annan fil.",
    attachments_unavailable: "Det går inte längre att skicka filer i chatten. Din text finns kvar.",
};
const hasText = computed(() => Boolean(message.value?.trim()));
const canSend = computed(
    () =>
        !submitting.value &&
        props.chatAvailableForCustomer &&
        !attachment.value?.uploading &&
        (hasText.value || Boolean(attachment.value?.key)) &&
        !(!props.isCustomer && props.replyAsEnabled && !replyAsUserId.value)
);
function growTextarea() {
    const textarea = textareaRef.value;
    if (!textarea) {
        return;
    }
    textarea.style.height = "auto";
    textarea.style.height = `${textarea.scrollHeight}px`;
}
function discardAttachment() {
    if (!attachment.value) {
        return;
    }
    attachment.value.upload?.abort();
    if (attachment.value.previewUrl) {
        URL.revokeObjectURL(attachment.value.previewUrl);
    }
    attachment.value = null;
}
function removeAttachment() {
    discardAttachment();
    errorMessage.value = null;
}
async function pickAttachment(event) {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) {
        return;
    }
    discardAttachment();
    errorMessage.value = null;
    const selection = reactive({
        name: file.name,
        size: file.size,
        previewUrl: file.type.startsWith("image/") ? URL.createObjectURL(file) : null,
        progress: 0,
        uploading: true,
        key: null,
        upload: null,
    });
    attachment.value = selection;
    try {
        const response = await axios.post(
            route("quote.chatMessages.attachmentUploads.store", {
                quoteKey: props.quoteKey,
                is_template: props.isTemplate,
            }),
            { name: file.name, type: file.type, size: file.size }
        );
        if (attachment.value !== selection) {
            return;
        }
        selection.upload = uploadToPresignedUrl(
            response.data.url,
            response.data.headers,
            file,
            (progress) => {
                selection.progress = progress;
            }
        );
        await selection.upload.done;
        selection.key = response.data.key;
        selection.progress = 1;
        selection.uploading = false;
        selection.upload = null;
    } catch (error) {
        if (attachment.value !== selection) {
            return;
        }
        discardAttachment();
        errorMessage.value =
            applyAttachmentFailure(error) ?? "Filen kunde inte laddas upp. Försök igen.";
    }
}
function firstError(error, field) {
    return error?.response?.data?.errors?.[field]?.[0] ?? null;
}
function applyAttachmentFailure(error) {
    const failure = error?.response?.data?.error;
    if (failure?.code === "file_rejected" || failure?.code === "attachments_unavailable") {
        discardAttachment();
    }
    if (failure?.code === "attachments_unavailable") {
        attachmentsBecameUnavailable.value = true;
    }
    if (failure?.code === "file_rejected" && failure.message) {
        return failure.message;
    }

    return failureMessages[failure?.code] ?? null;
}
async function submit() {
    if (!canSend.value) {
        return;
    }
    submitting.value = true;
    errorMessage.value = null;
    const payload = {
        is_customer_message: props.isCustomer,
        message: message.value,
        reply_as_user_id: replyAsUserId.value,
    };
    if (attachment.value?.key) {
        payload.attachment_key = attachment.value.key;
    }
    const serializedPayload = JSON.stringify(payload);
    if (idempotencyPayload.value !== serializedPayload) {
        idempotencyKey.value = uuidv4();
        idempotencyPayload.value = serializedPayload;
    }
    try {
        const response = await axios.post(
            route("quote.chatMessages.store", {
                quoteKey: props.quoteKey,
                is_template: props.isTemplate,
            }),
            payload,
            {
                headers: {
                    "Idempotency-Key": idempotencyKey.value,
                },
            }
        );
        idempotencyKey.value = null;
        idempotencyPayload.value = null;
        message.value = null;
        discardAttachment();
        replyAsUserId.value = props.replyAsDefaultUserId;
        replyAsPickerKey.value++;
        emit("submitted", response.data);
    } catch (error) {
        errorMessage.value =
            applyAttachmentFailure(error) ??
            firstError(error, "message") ??
            "Meddelandet kunde inte skickas. Din text finns kvar. Försök igen.";
    } finally {
        submitting.value = false;
    }
}
watch(
    () => props.replyAsDefaultUserId,
    (newValue) => {
        if (!replyAsUserId.value) {
            replyAsUserId.value = newValue;
        }
    }
);
onUnmounted(discardAttachment);
</script>
